import type { JSONSchemaType } from 'ajv'
import type { ActionDefinition, ActionOutcome } from '../action'
import { getAction, registerAction } from '../registry'
import {
  postStockMovements,
  setStockReorderPoints,
  createStockLocation,
  renameStockLocation,
  archiveStockLocation,
  StockLedgerClientError,
  type PostStockMovementInput,
} from '../../stock/stockLedgerService'
import type { StockLocationKind } from '../../../types/stock'
import { isWorkspaceRole, roleCanPerform } from '../../../utils/workspacePermissions'

export const STOCK_POST_MOVEMENTS_ACTION_ID = 'stock.postMovements'
export const STOCK_SET_REORDER_POINTS_ACTION_ID = 'stock.setReorderPoints'
export const STOCK_LOCATION_CREATE_ACTION_ID = 'stock.location.create'
export const STOCK_LOCATION_RENAME_ACTION_ID = 'stock.location.rename'
export const STOCK_LOCATION_ARCHIVE_ACTION_ID = 'stock.location.archive'

export type ManualMovementType =
  | 'IN'
  | 'OUT'
  | 'COUNT'
  | 'ADJUST'
  | 'WRITE_OFF'
  | 'RESERVE'
  | 'RELEASE'
  | 'CONSUME'

export interface StockMovementItemArg {
  itemId: string
  type: ManualMovementType
  quantity?: number
  delta?: number
  locationId?: string
  lotId?: string
  reason?: string
  note?: string
}

export interface StockPostMovementsArgs {
  movements: StockMovementItemArg[]
}

const movementItemSchema: JSONSchemaType<StockMovementItemArg> = {
  type: 'object',
  properties: {
    itemId: { type: 'string', minLength: 1 },
    type: {
      type: 'string',
      enum: ['IN', 'OUT', 'COUNT', 'ADJUST', 'WRITE_OFF', 'RESERVE', 'RELEASE', 'CONSUME'],
    },
    quantity: { type: 'number', nullable: true },
    delta: { type: 'number', nullable: true },
    locationId: { type: 'string', nullable: true },
    lotId: { type: 'string', nullable: true },
    reason: { type: 'string', nullable: true },
    note: { type: 'string', nullable: true },
  },
  required: ['itemId', 'type'],
  additionalProperties: false,
}

const postMovementsArgsSchema: JSONSchemaType<StockPostMovementsArgs> = {
  type: 'object',
  properties: {
    movements: {
      type: 'array',
      items: movementItemSchema,
      minItems: 1,
      maxItems: 100,
    },
  },
  required: ['movements'],
  additionalProperties: false,
}

export interface StockSetReorderPointsArgs {
  itemId: string
  patch: Record<string, number | null>
}

const setReorderPointsArgsSchema: JSONSchemaType<StockSetReorderPointsArgs> = {
  type: 'object',
  properties: {
    itemId: { type: 'string', minLength: 1 },
    patch: {
      type: 'object',
      required: [],
      minProperties: 1,
      maxProperties: 200,
      propertyNames: {
        pattern: '^[A-Za-z0-9_-]{1,128}$',
      },
      additionalProperties: {
        type: 'number',
        minimum: 0,
        nullable: true,
      },
    },
  },
  required: ['itemId', 'patch'],
  additionalProperties: false,
}

export interface StockLocationCreateArgs {
  name: string
  kind: StockLocationKind
}

const locationCreateArgsSchema: JSONSchemaType<StockLocationCreateArgs> = {
  type: 'object',
  properties: {
    name: { type: 'string', minLength: 1, maxLength: 80 },
    kind: { type: 'string', enum: ['STORE', 'VEHICLE', 'SITE', 'OTHER'] },
  },
  required: ['name', 'kind'],
  additionalProperties: false,
}

export interface StockLocationRenameArgs {
  locationId: string
  name: string
}

const locationRenameArgsSchema: JSONSchemaType<StockLocationRenameArgs> = {
  type: 'object',
  properties: {
    locationId: { type: 'string', minLength: 1 },
    name: { type: 'string', minLength: 1, maxLength: 80 },
  },
  required: ['locationId', 'name'],
  additionalProperties: false,
}

export interface StockLocationArchiveArgs {
  locationId: string
}

const locationArchiveArgsSchema: JSONSchemaType<StockLocationArchiveArgs> = {
  type: 'object',
  properties: {
    locationId: { type: 'string', minLength: 1 },
  },
  required: ['locationId'],
  additionalProperties: false,
}

const stockPermission = (context: { actor: { role?: unknown } }): boolean =>
  isWorkspaceRole(context.actor.role) ? roleCanPerform(context.actor.role, 'EDITOR') : false

const formatActionError = (err: unknown): string => {
  if (err instanceof StockLedgerClientError) {
    return err.message.includes(err.code) ? err.message : `${err.code}: ${err.message}`
  }
  return err instanceof Error ? err.message : String(err)
}

const postStockMovementsAction: ActionDefinition<StockPostMovementsArgs> = {
  id: STOCK_POST_MOVEMENTS_ACTION_ID,
  label: 'Post stock movements',
  description: 'Posts stock ledger movements (IN, OUT, COUNT, etc.) to the inventory ledger.',
  risk: 'HIGH',
  domain: 'stock',
  surfaces: ['catalog'],
  argsSchema: postMovementsArgsSchema,
  permission: stockPermission,
  run: async (args, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'stock-correlation'
    if (!context.workspaceId) {
      const message = 'Workspace ID is required for stock movements'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await postStockMovements(
        context.workspaceId,
        args.movements as PostStockMovementInput[],
      )
      const pendingCount = res.pendingApprovals?.length ?? 0
      if (pendingCount > 0) {
        return {
          kind: 'NEEDS_CONFIRMATION',
          message: `${pendingCount} movement(s) waiting for Admin approval.`,
          confirmPayload: res.pendingApprovals,
          correlationId,
        }
      }
      return {
        kind: 'COMPLETED',
        message: `Posted ${args.movements.length} stock movement(s).`,
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = formatActionError(err)
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: true,
        correlationId,
      }
    }
  },
}

const setStockReorderPointsAction: ActionDefinition<StockSetReorderPointsArgs> = {
  id: STOCK_SET_REORDER_POINTS_ACTION_ID,
  label: 'Set stock reorder points',
  description: 'Updates location reorder points on a stock item.',
  risk: 'MEDIUM',
  domain: 'stock',
  surfaces: ['catalog'],
  argsSchema: setReorderPointsArgsSchema,
  permission: stockPermission,
  run: async (args, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'stock-correlation'
    if (!context.workspaceId) {
      const message = 'Workspace ID is required for setting reorder points'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      await setStockReorderPoints(context.workspaceId, args.itemId, args.patch)
      return {
        kind: 'COMPLETED',
        message: `Updated reorder points for item ${args.itemId}.`,
        result: undefined,
        correlationId,
      }
    } catch (err) {
      const message = formatActionError(err)
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: true,
        correlationId,
      }
    }
  },
}

const createStockLocationAction: ActionDefinition<StockLocationCreateArgs> = {
  id: STOCK_LOCATION_CREATE_ACTION_ID,
  label: 'Create stock location',
  description: 'Creates a new custom stock location in the workspace.',
  risk: 'MEDIUM',
  domain: 'stock',
  surfaces: ['catalog'],
  argsSchema: locationCreateArgsSchema,
  permission: stockPermission,
  run: async (args, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'stock-correlation'
    if (!context.workspaceId) {
      const message = 'Workspace ID is required for creating a stock location'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await createStockLocation(
        context.workspaceId,
        { name: args.name, kind: args.kind },
        context.actor.uid,
      )
      return {
        kind: 'COMPLETED',
        message: `Created stock location "${args.name}".`,
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = formatActionError(err)
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: true,
        correlationId,
      }
    }
  },
}

const renameStockLocationAction: ActionDefinition<StockLocationRenameArgs> = {
  id: STOCK_LOCATION_RENAME_ACTION_ID,
  label: 'Rename stock location',
  description: 'Renames an existing custom stock location.',
  risk: 'MEDIUM',
  domain: 'stock',
  surfaces: ['catalog'],
  argsSchema: locationRenameArgsSchema,
  permission: stockPermission,
  run: async (args, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'stock-correlation'
    try {
      await renameStockLocation(args.locationId, args.name, context.actor.uid)
      return {
        kind: 'COMPLETED',
        message: `Renamed location to "${args.name}".`,
        result: undefined,
        correlationId,
      }
    } catch (err) {
      const message = formatActionError(err)
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: true,
        correlationId,
      }
    }
  },
}

const archiveStockLocationAction: ActionDefinition<StockLocationArchiveArgs> = {
  id: STOCK_LOCATION_ARCHIVE_ACTION_ID,
  label: 'Archive stock location',
  description: 'Archives an existing custom stock location.',
  risk: 'MEDIUM',
  domain: 'stock',
  surfaces: ['catalog'],
  argsSchema: locationArchiveArgsSchema,
  permission: stockPermission,
  run: async (args, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'stock-correlation'
    try {
      await archiveStockLocation(args.locationId, context.actor.uid)
      return {
        kind: 'COMPLETED',
        message: `Archived location ${args.locationId}.`,
        result: undefined,
        correlationId,
      }
    } catch (err) {
      const message = formatActionError(err)
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: true,
        correlationId,
      }
    }
  },
}

export const registerStockActions = (): void => {
  if (!getAction(STOCK_POST_MOVEMENTS_ACTION_ID)) registerAction(postStockMovementsAction)
  if (!getAction(STOCK_SET_REORDER_POINTS_ACTION_ID)) registerAction(setStockReorderPointsAction)
  if (!getAction(STOCK_LOCATION_CREATE_ACTION_ID)) registerAction(createStockLocationAction)
  if (!getAction(STOCK_LOCATION_RENAME_ACTION_ID)) registerAction(renameStockLocationAction)
  if (!getAction(STOCK_LOCATION_ARCHIVE_ACTION_ID)) registerAction(archiveStockLocationAction)
}

registerStockActions()
